/**
 * Special Menus Admin Page
 * 
 * Allows Tammy to create and manage themed menus:
 * - Valentine's Day Menu
 * - Birthday Party Packages
 * - Holiday Specials
 * - etc.
 * 
 * Each special menu can be toggled on/off to show on the website.
 */

import { useState, useEffect, useRef } from 'react';
import { 
  HiPlus, 
  HiPencil, 
  HiTrash, 
  HiRefresh,
  HiCollection,
  HiCalendar,
  HiX,
  HiChevronDown,
  HiChevronUp,
  HiUpload,
  HiPhotograph,
  HiExternalLink,
  HiDuplicate
} from 'react-icons/hi';
import Tooltip, { InfoTooltip } from '../../components/Tooltip';
import ImageGalleryPicker from '../components/ImageGalleryPicker';
import { localYyyyMmDd } from '../../utils/calendarDate';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// API URL
const API_URL = import.meta.env.VITE_API_URL || '';

// Collection type
interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  is_active: boolean;
  show_on_homepage: boolean;
  display_order: number;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  items?: CollectionItem[];
}

interface CollectionItem {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  category: string;
}

interface MenuItem {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  category: string;
}

// Form state
interface CollectionForm {
  name: string;
  slug: string;
  description: string;
  image_url: string;
  is_active: boolean;
  show_on_homepage: boolean;
  display_order: string;
  start_date: string;
  end_date: string;
}

const emptyForm: CollectionForm = {
  name: '',
  slug: '',
  description: '',
  image_url: '',
  is_active: false,
  show_on_homepage: true,
  display_order: '100',
  start_date: '',
  end_date: '',
};

export default function Collections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CollectionForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [imageTab, setImageTab] = useState<'upload' | 'browse' | 'url'>('upload');
  
  // Item management state
  const [expandedCollection, setExpandedCollection] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isAddingItems, setIsAddingItems] = useState(false);

  // Fetch collections
  const fetchCollections = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch(`${API_URL}/api/collections`);
      if (!response.ok) throw new Error('Failed to fetch collections');
      const data = await response.json();
      setCollections(data.collections || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  // Fetch menu items (for adding to collections)
  const fetchMenuItems = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/menu`);
      if (!response.ok) return;
      const data = await response.json();
      setMenuItems(data.items || []);
    } catch (err) {
      console.error('Failed to fetch menu items:', err);
    }
  };

  // Fetch collection details (with items)
  const fetchCollectionDetails = async (id: string) => {
    try {
      const response = await adminFetch(`${API_URL}/api/collections/${id}`);
      if (!response.ok) return;
      const data = await response.json();
      
      // Update collection with items
      setCollections(prev => prev.map(c => 
        c.id === id ? { ...c, items: data.items || [] } : c
      ));
    } catch (err) {
      console.error('Failed to fetch collection details:', err);
    }
  };

  // Load on mount
  useEffect(() => {
    fetchCollections();
    fetchMenuItems();
  }, []);

  // Auto-expand collections with no items (helps users understand they need to add items)
  useEffect(() => {
    if (!loading && collections.length > 0) {
      // Find a collection with no items and expand it to help user see they need to add items
      const emptyCollection = collections.find(c => c.is_active && (!c.items || c.items.length === 0));
      if (emptyCollection && !expandedCollection) {
        // Auto-expand and fetch details
        setExpandedCollection(emptyCollection.id);
        fetchCollectionDetails(emptyCollection.id);
      }
    }
  }, [loading, collections.length]);

  // Toggle collection active status
  const toggleActive = async (id: string) => {
    try {
      const response = await adminFetch(`${API_URL}/api/collections/${id}/toggle`, {
        method: 'PUT',
      });
      if (!response.ok) throw new Error('Failed to toggle');
      
      // Update local state
      setCollections(prev => prev.map(c => 
        c.id === id ? { ...c, is_active: !c.is_active } : c
      ));
    } catch (err) {
      console.error('Failed to toggle collection:', err);
      setError('Failed to toggle collection. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Delete collection
  const duplicateCollection = async (id: string) => {
    try {
      const response = await adminFetch(`${API_URL}/api/collections/${id}/duplicate`, { method: 'POST' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to duplicate');
      }
      const created = await response.json();
      await fetchCollections();
      openEditForm(created);
    } catch (err) {
      console.error('Failed to duplicate collection', err);
      setError(err instanceof Error ? err.message : 'Failed to duplicate collection.');
    }
  };

  const deleteCollection = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/collections/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete');
      
      setCollections(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete collection:', err);
      setError('Failed to delete collection. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Open form for editing
  const openEditForm = (collection: Collection) => {
    setEditingId(collection.id);
    setForm({
      name: collection.name,
      slug: collection.slug,
      description: collection.description || '',
      image_url: collection.image_url || '',
      is_active: collection.is_active,
      show_on_homepage: collection.show_on_homepage,
      display_order: collection.display_order.toString(),
      start_date: collection.start_date || '',
      end_date: collection.end_date || '',
    });
    setIsFormOpen(true);
  };

  // Open form for new collection
  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsFormOpen(true);
  };

  // Close form
  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  // Save collection
  const saveCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingId 
        ? `${API_URL}/api/collections/${editingId}` 
        : `${API_URL}/api/collections`;
      const method = editingId ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: form.name,
          slug: form.slug || undefined,
          description: form.description || null,
          image_url: form.image_url || null,
          is_active: form.is_active,
          show_on_homepage: form.show_on_homepage,
          display_order: parseInt(form.display_order) || 100,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save');
      }

      await fetchCollections();
      closeForm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save collection';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Expand/collapse collection items
  const toggleExpand = async (id: string) => {
    if (expandedCollection === id) {
      setExpandedCollection(null);
    } else {
      setExpandedCollection(id);
      await fetchCollectionDetails(id);
    }
  };

  // Add items to collection
  const addItemsToCollection = async (collectionId: string) => {
      if (selectedItems.length === 0) {
        setError('Please select at least one item to add to the collection.');
        setTimeout(() => setError(null), 5000);
        return;
      }

    try {
      const response = await adminFetch(`${API_URL}/api/collections/${collectionId}/items`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ menu_item_ids: selectedItems }),
      });

      if (!response.ok) throw new Error('Failed to add items');

      // Refresh collection details
      await fetchCollectionDetails(collectionId);
      setSelectedItems([]);
      setIsAddingItems(false);
    } catch (err) {
      console.error('Failed to add items:', err);
      setError('Failed to add items to collection. Please try again.');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Remove item from collection
  const removeItemFromCollection = async (collectionId: string, itemId: string) => {
    try {
      const response = await adminFetch(`${API_URL}/api/collections/${collectionId}/items/${itemId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to remove item');

      // Update local state
      setCollections(prev => prev.map(c => 
        c.id === collectionId 
          ? { ...c, items: c.items?.filter(i => i.id !== itemId) }
          : c
      ));
    } catch (err) {
      console.error('Failed to remove item:', err);
      setError('Failed to remove item from collection');
      setTimeout(() => setError(null), 5000);
    }
  };


  // Handle collection banner image upload
  const handleBannerImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPEG, PNG, WebP, GIF, or HEIC from iPhone)');
      setTimeout(() => setUploadError(null), 5000);
      return;
    }

    // Validate file size (20MB max - server will optimize)
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('Image must be smaller than 20MB');
      setTimeout(() => setUploadError(null), 5000);
      return;
    }

    setUploadingImage(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Use relative path for upload (same origin in production)
      const uploadUrl = API_URL ? `${API_URL}/api/upload` : '/api/upload';
      const response = await adminFetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      
      if (!data.url) {
        throw new Error('Server did not return image URL');
      }
      
      // Update form with uploaded image URL
      setForm(prev => ({
        ...prev,
        image_url: data.url,
      }));

      // Show success message briefly
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      console.error('Upload error:', err);
      setUploadError(`Upload failed: ${errorMessage}. Please try again or use a different image.`);
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (imageFileInputRef.current) {
        imageFileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiCollection className="w-7 h-7 text-purple-600" />
            Special Menus
          </h1>
          <p className="text-gray-500 mt-1">
            Create themed menus and turn them on/off seasonally
          </p>
        </div>
        <div className="flex gap-2">
          <Tooltip content="Refresh the list to see any changes you made or new collections.">
            <button
              onClick={fetchCollections}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 
                         rounded-lg hover:bg-gray-50 transition-colors"
              title="Refresh"
            >
              <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>
          <Tooltip content="Create a themed menu like 'Valentine's Day' or 'Birthday Parties'. You can add products and turn it on/off anytime.">
            <button
              onClick={openNewForm}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 
                         text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <HiPlus className="w-5 h-5" />
              New Special Menu
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <h3 className="font-semibold text-purple-800 mb-2">💡 How Special Menus Work</h3>
        <ul className="text-sm text-purple-700 space-y-1 mb-3">
          <li>• Create a collection (e.g., "Valentine's Day Menu")</li>
          <li>• Add menu items to it</li>
          <li>• Toggle ON to show on website, OFF to hide</li>
          <li>• Set dates to auto-show/hide seasonally</li>
        </ul>
        <div className="bg-white rounded-lg p-3 border border-purple-200">
          <h4 className="font-semibold text-purple-800 mb-2 text-xs uppercase">✅ Special Menu Visibility Requirements</h4>
          <ul className="text-xs text-purple-700 space-y-1">
            <li>✓ <strong>Toggle must be ON</strong> (green switch) - Shows "LIVE" badge</li>
            <li>✓ <strong>Date range must be valid</strong> - Start date ≤ today ≤ End date (or no dates set)</li>
            <li>✓ <strong>Add items</strong> - Special menu needs at least one product to be useful</li>
            <li>✓ <strong>"Show on homepage"</strong> - Check this to appear in "Seasonal Specials" section</li>
          </ul>
          <p className="text-xs text-purple-600 mt-2 italic">
            💡 If your collection says "LIVE" but isn't showing, check the dates or click the 🔗 icon to test the direct link!
          </p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <div className="flex items-center gap-2">
            <button onClick={fetchCollections} className="underline text-sm">Try again</button>
            <button 
              onClick={() => setError(null)} 
              className="text-red-500 hover:text-red-700"
              aria-label="Dismiss error"
            >
              <HiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading collections...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && collections.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-semibold text-gray-900">No collections yet</h3>
          <p className="text-gray-500 mt-2">
            Create your first themed menu to get started.
          </p>
          <button
            onClick={openNewForm}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-600 
                       text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <HiPlus className="w-5 h-5" />
            Create Special Menu
          </button>
        </div>
      )}

      {/* Collections List */}
      {!loading && collections.length > 0 && (
        <div className="space-y-4">
          {collections.map(collection => (
            <div
              key={collection.id}
              className={`bg-white rounded-xl border overflow-hidden transition-all
                          ${collection.is_active 
                            ? 'border-green-300 shadow-md' 
                            : 'border-gray-200'}`}
            >
              {/* Collection Header */}
              <div className="p-4 flex items-center gap-4">
                {/* Image */}
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {collection.image_url ? (
                    <img
                      src={collection.image_url}
                      alt={collection.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">
                      📋
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{collection.name}</h3>
                    {collection.is_active && (() => {
                      // Check if collection is actually visible (within date range)
                      const now = localYyyyMmDd();
                      const startOk = !collection.start_date || collection.start_date <= now;
                      const endOk = !collection.end_date || collection.end_date >= now;
                      const isVisible = startOk && endOk;
                      
                      if (isVisible) {
                        return (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                            LIVE
                          </span>
                        );
                      } else {
                        return (
                          <Tooltip content={!startOk 
                            ? `Special menu starts on ${collection.start_date}. It will be visible after that date.`
                            : `Special menu ended on ${collection.end_date}. Update the end date to make it visible again.`}>
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                              SCHEDULED
                            </span>
                          </Tooltip>
                        );
                      }
                    })()}
                  </div>
                  {collection.description && (
                    <p className="text-sm text-gray-500 line-clamp-1">{collection.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span>/{collection.slug}</span>
                    {collection.start_date && (
                      <span className="flex items-center gap-1">
                        <HiCalendar className="w-3 h-3" />
                        {collection.start_date} - {collection.end_date || '∞'}
                      </span>
                    )}
                    {/* Visibility Status Warning */}
                    {collection.is_active && (() => {
                      const now = localYyyyMmDd();
                      const startOk = !collection.start_date || collection.start_date <= now;
                      const endOk = !collection.end_date || collection.end_date >= now;
                      const isVisible = startOk && endOk;
                      const itemCount = collection.items?.length || 0;
                      
                      if (!isVisible) {
                        return (
                          <span className="text-yellow-600 font-medium">
                            {!startOk ? `Starts ${collection.start_date}` : `Ended ${collection.end_date}`}
                          </span>
                        );
                      } else if (itemCount === 0) {
                        return (
                          <Tooltip content="This collection has no products yet. Add items to make it useful!">
                            <span className="text-orange-600 font-medium">⚠️ No items</span>
                          </Tooltip>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* View on Site Button - Only show if active and visible */}
                  {collection.is_active && (() => {
                    const now = localYyyyMmDd();
                    const startOk = !collection.start_date || collection.start_date <= now;
                    const endOk = !collection.end_date || collection.end_date >= now;
                    const isVisible = startOk && endOk;
                    
                    if (isVisible) {
                      const siteUrl = window.location.origin.replace('/admin', '');
                      return (
                        <Tooltip content="Click to view this collection on your live website">
                          <a
                            href={`${siteUrl}/menu/${collection.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600"
                            title="View on Site"
                            aria-label="View collection on website"
                          >
                            <HiExternalLink className="w-5 h-5" />
                          </a>
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Toggle Active */}
                  <Tooltip 
                    position="bottom-right"
                    content={collection.is_active 
                      ? "Special menu is LIVE on your website. Click to hide it from customers." 
                      : "Special menu is hidden. Click to make it LIVE so customers can see it on your homepage and website."}>
                    <button
                      onClick={() => toggleActive(collection.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                 ${collection.is_active 
                                   ? 'bg-green-500' 
                                   : 'bg-gray-300'}`}
                      title={collection.is_active ? 'Turn OFF' : 'Turn ON'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                   ${collection.is_active ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </Tooltip>

                  {/* Expand Items - Clear labeled button */}
                  <button
                    onClick={() => toggleExpand(collection.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                               ${expandedCollection === collection.id 
                                 ? 'bg-purple-100 text-purple-700' 
                                 : 'bg-gray-100 text-gray-700 hover:bg-purple-50 hover:text-purple-600'}`}
                  >
                    {expandedCollection === collection.id ? (
                      <>
                        <HiChevronUp className="w-4 h-4" />
                        Hide Items
                      </>
                    ) : (
                      <>
                        <HiChevronDown className="w-4 h-4" />
                        View Items ({collection.items?.length || 0})
                      </>
                    )}
                  </button>

                  {/* Edit */}
                  <Tooltip position="bottom-right" content="Edit the collection name, description, banner image, dates, and settings.">
                    <button
                      onClick={() => openEditForm(collection)}
                      className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <HiPencil className="w-5 h-5" />
                    </button>
                  </Tooltip>

                  {/* Duplicate */}
                  <Tooltip position="bottom-right" content="Duplicate this collection (copy name, items, and settings). The copy starts inactive.">
                    <button
                      onClick={() => duplicateCollection(collection.id)}
                      className="p-2 hover:bg-gray-100 text-gray-600 rounded-lg transition-colors"
                      title="Duplicate"
                    >
                      <HiDuplicate className="w-5 h-5" />
                    </button>
                  </Tooltip>

                  {/* Delete */}
                  <Tooltip position="bottom-right" content="Permanently delete this collection. This won't delete the products, just removes them from this collection.">
                    <button
                      onClick={() => deleteCollection(collection.id, collection.name)}
                      className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <HiTrash className="w-5 h-5" />
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* Expanded Items Section */}
              {expandedCollection === collection.id && (
                <div className="border-t bg-gray-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-700">
                      Items in this collection ({collection.items?.length || 0})
                    </h4>
                    <Tooltip content="Add products from your menu to this collection. Products can be in multiple collections!">
                      <button
                        onClick={() => setIsAddingItems(!isAddingItems)}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {isAddingItems ? 'Cancel' : '+ Add Items'}
                      </button>
                    </Tooltip>
                  </div>

                  {/* Add Items UI */}
                  {isAddingItems && (
                    <div className="bg-white rounded-lg border p-4 mb-4">
                      <div className="mb-3">
                        <p className="text-sm text-gray-600 mb-2">Select items to add:</p>
                        {/* Helpful message about creating products */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                          <p className="text-sm text-blue-800">
                            <strong>💡 Need to create a new product?</strong> Go to{' '}
                            <a 
                              href="/admin/menu/new" 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 underline font-medium"
                            >
                              Menu → Add Item
                              <HiExternalLink className="w-3 h-3" />
                            </a>
                            {' '}to create it, then come back here to add it to this collection.
                          </p>
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-2 mb-3">
                        {menuItems
                          .filter(item => !collection.items?.some(ci => ci.id === item.id))
                          .map(item => (
                            <label
                              key={item.id}
                              className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(item.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems([...selectedItems, item.id]);
                                  } else {
                                    setSelectedItems(selectedItems.filter(id => id !== item.id));
                                  }
                                }}
                                className="w-4 h-4 text-purple-600"
                              />
                              {item.image_url && (
                                <img src={item.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                              )}
                              <span className="text-sm">{item.name}</span>
                              <span className="text-xs text-gray-400">${item.price.toFixed(2)}</span>
                            </label>
                          ))}
                      </div>
                      <Tooltip content={selectedItems.length === 0 
                        ? "Select products from the list above, then click here to add them to this collection." 
                        : `Click to add ${selectedItems.length} selected product${selectedItems.length !== 1 ? 's' : ''} to this collection.`}>
                        <button
                          onClick={() => addItemsToCollection(collection.id)}
                          disabled={selectedItems.length === 0}
                          className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
                                     disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Add {selectedItems.length} Item{selectedItems.length !== 1 ? 's' : ''}
                        </button>
                      </Tooltip>
                    </div>
                  )}

                  {/* Current Items */}
                  {collection.items && collection.items.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {collection.items.map(item => (
                        <div
                          key={item.id}
                          className="bg-white rounded-lg border p-2 relative group"
                        >
                          <button
                            onClick={() => removeItemFromCollection(collection.id, item.id)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                                       flex items-center justify-center opacity-0 group-hover:opacity-100
                                       transition-opacity hover:bg-red-600"
                            title="Remove"
                          >
                            <HiX className="w-4 h-4" />
                          </button>
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full aspect-square object-cover rounded mb-2"
                            />
                          ) : (
                            <div className="w-full aspect-square bg-gray-100 rounded mb-2 flex items-center justify-center text-2xl">
                              🧁
                            </div>
                          )}
                          <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                          <p className="text-xs text-gray-500">${item.price.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No items in this collection yet. Click "Add Items" to get started.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeForm} />
            
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
              <button
                onClick={closeForm}
                className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded"
                title="Close"
                aria-label="Close dialog"
              >
                <HiX className="w-5 h-5 text-gray-500" />
              </button>

              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingId ? 'Edit Special Menu' : 'New Special Menu'}
              </h2>

              <form onSubmit={saveCollection} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    Special Menu Name *
                    <InfoTooltip content="Give your collection a clear name like 'Valentine's Day Menu' or 'Birthday Party Packages'. This name shows on your website." />
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Valentine's Day Menu"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg
                               focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    URL Slug
                    <InfoTooltip content="The URL-friendly name for this collection. Customers will access it at /menu/[slug]. Leave empty to auto-generate from the collection name." />
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">/menu/</span>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                      placeholder="valentines"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg
                                 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate from name</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    Description
                    <InfoTooltip content="Add a short description that explains what this collection is about. This appears on the collection page and helps customers understand what they'll find." />
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Describe this collection..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg
                               focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Banner Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    Banner Image
                    <InfoTooltip content="This image shows as a large banner at the top of the collection page and as a card image on your homepage. Upload a beautiful photo that represents this collection!" />
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    This image appears as a banner on the collection page and as a card image on the homepage
                  </p>
                  
                  {/* Upload Success Message */}
                  {uploadSuccess && (
                    <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      <span className="text-sm text-green-700">Image uploaded successfully!</span>
                    </div>
                  )}

                  {/* Upload Error Message */}
                  {uploadError && (
                    <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                      <span className="text-red-600">✗</span>
                      <span className="text-sm text-red-700">{uploadError}</span>
                    </div>
                  )}
                  
                  {/* Image Selection Tabs */}
                  <div className="space-y-3">
                    {/* Tab Buttons */}
                    <div className="flex gap-2 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={() => setImageTab('upload')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          imageTab === 'upload'
                            ? 'border-purple-600 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <HiUpload className="w-4 h-4 inline mr-1" />
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageTab('browse')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          imageTab === 'browse'
                            ? 'border-purple-600 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <HiPhotograph className="w-4 h-4 inline mr-1" />
                        Browse Existing
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageTab('url')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          imageTab === 'url'
                            ? 'border-purple-600 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <HiExternalLink className="w-4 h-4 inline mr-1" />
                        Paste URL
                      </button>
                    </div>

                    {/* Upload Tab */}
                    {imageTab === 'upload' && (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            ref={imageFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleBannerImageUpload}
                            className="hidden"
                            id="collection-image-upload"
                            aria-label="Upload banner image"
                          />
                          {uploadingImage ? (
                            <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 
                                           border-2 border-dashed border-gray-300 rounded-lg">
                              <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                              <span className="text-purple-600 font-medium">Uploading...</span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => imageFileInputRef.current?.click()}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 
                                         border-2 border-dashed border-gray-300 rounded-lg cursor-pointer
                                         hover:border-purple-400 hover:bg-purple-50 transition-colors
                                         bg-white"
                            >
                              <HiUpload className="w-5 h-5 text-purple-600" />
                              <span className="text-gray-600">
                                <span className="text-purple-600 font-medium">Click to upload</span> banner image
                              </span>
                            </button>
                          )}
                        </div>
                        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                          <span className="text-blue-500 mt-0.5">ℹ️</span>
                          <p className="text-blue-700">
                            <strong>Tip:</strong> Images are automatically optimized for fast loading. 
                            Supports all image formats including iPhone photos (HEIC).
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Browse Tab */}
                    {imageTab === 'browse' && (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setShowImageGallery(true)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 
                                     border-2 border-dashed border-purple-300 rounded-lg cursor-pointer
                                     hover:border-purple-400 hover:bg-purple-50 transition-colors
                                     bg-white"
                        >
                          <HiPhotograph className="w-5 h-5 text-purple-600" />
                          <span className="text-gray-600">
                            <span className="text-purple-600 font-medium">Browse</span> existing images
                          </span>
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          Choose from menu items, gallery photos, or collection banners you've already uploaded
                        </p>
                      </div>
                    )}

                    {/* URL Tab */}
                    {imageTab === 'url' && (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={form.image_url}
                          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg
                                     focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500">
                          Paste a direct link to an image from the web
                        </p>
                      </div>
                    )}

                    {/* Image preview */}
                    {form.image_url && (
                      <div className="relative inline-block mt-3">
                        <img 
                          src={form.image_url} 
                          alt="Banner preview" 
                          className="w-full max-w-md h-48 object-cover rounded-lg border"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, image_url: '' })}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                                     flex items-center justify-center hover:bg-red-600 transition-colors"
                          title="Remove image"
                          aria-label="Remove image"
                        >
                          <HiX className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Image Gallery Picker Modal */}
                  <ImageGalleryPicker
                    isOpen={showImageGallery}
                    onClose={() => setShowImageGallery(false)}
                    onSelect={(imageUrl) => {
                      setForm({ ...form, image_url: imageUrl });
                      setUploadSuccess(true);
                      setTimeout(() => setUploadSuccess(false), 3000);
                    }}
                    currentImageUrl={form.image_url}
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="collection-start-date" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      Start Date (optional)
                      <InfoTooltip content="Set a date to automatically show this collection on your website. Leave empty to show it manually with the toggle switch." />
                    </label>
                    <input
                      id="collection-start-date"
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg
                                 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      aria-label="Special menu start date"
                    />
                  </div>
                  <div>
                    <label htmlFor="collection-end-date" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      End Date (optional)
                      <InfoTooltip content="Set a date to automatically hide this collection after the season ends. Great for holiday menus!" />
                    </label>
                    <input
                      id="collection-end-date"
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg
                                 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      aria-label="Special menu end date"
                    />
                  </div>
                </div>

                {/* Toggles */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Show on website</span>
                    <InfoTooltip content="When checked, this collection will be visible to customers on your website. You can also toggle this on/off later with the switch on the collection card." />
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.show_on_homepage}
                      onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Show on homepage</span>
                    <InfoTooltip content="When checked, this collection appears in the 'Seasonal Specials' section on your homepage. Uncheck to hide it from the homepage but still allow direct access via URL." />
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg
                               hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg
                               hover:bg-purple-700 transition-colors font-medium
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
